import { Grid } from "@material-ui/core";
import { AutocompleteInputChangeReason } from "@material-ui/lab";
import { formatIncompletePhoneNumber } from "libphonenumber-js/min";
import * as React from "react";

import { SelectInput, useRelodeApp } from "../";
import { domain } from "../../";
import { Stakeholder } from "../../domain";
import { useDebounce } from "../hooks";

interface StakeholderSearchProps {
    onSelect: (pos: Stakeholder | undefined) => void;
    onFocus?: (e) => void;
    onBlur?: (e) => void;

    error?: boolean;
    helperText?: string;
    selectedStakeholder?: Stakeholder;
    roles: domain.UserRole[];
    label?: string;
    disabled?: boolean;
    clearable?: boolean;
    clearInputOnAdd?: boolean;
}

export const StakeholderSearch = (props: StakeholderSearchProps) => {
    const { selectedStakeholder, roles, disabled, clearable = true } = props;

    const {
        state: {
            config: { stakeholderLookupService },
        },
    } = useRelodeApp();

    const [suggestions, setSuggestions] = React.useState<Stakeholder[]>([]);

    const debounceStakeholderSuggestion = useDebounce(
        async (searchText: string) =>
            await stakeholderLookupService.loadSuggestions(
                searchText,
                roles.map(r => r.roleId)
            ),
        500,
        []
    );

    const handleInputChange = async (
        event: never,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (reason === "reset") return;
        if (!value || value.trim() === "" || value.trim().length === 0) {
            setSuggestions([]);
            return;
        }
        const _suggestions = await debounceStakeholderSuggestion(value);
        setSuggestions(_suggestions);
    };

    const handleSelectionChanged = (e: never, newValue: Stakeholder) => {
        props.onSelect(newValue);
    };

    const getOptionLabel = (option: Stakeholder) => {
        if (!option?.role?.roleAbbreviation || !option?.name) return;

        const roleAbbreviation = "(" + option.role.roleAbbreviation + ")";

        return [option.name, roleAbbreviation].filter(o => !!o).join(" ");
    };

    const getOptionEmail = (option: Stakeholder) =>
        [option.email?.toLowerCase()].filter(o => !!o).join("");

    const getOptionPhoneNumber = (option: Stakeholder) =>
        [
            option.phoneNumber
                ? formatIncompletePhoneNumber(option.phoneNumber, "US")
                : undefined,
        ]
            .filter(o => !!o)
            .join("");

    return (
        <div>
            <SelectInput
                mode="suggest"
                label={props.label}
                error={props.error}
                helperText={props.helperText}
                options={suggestions ?? []}
                onChange={handleSelectionChanged}
                onInputChange={handleInputChange}
                getOptionLabel={getOptionLabel}
                disabled={disabled}
                noOptionsText={"Your keywords did not match any user"}
                renderOption={(o, s) => (
                    <Grid container>
                        <Grid item xs={12}>
                            {getOptionLabel(o)}
                        </Grid>
                        <Grid item xs={12} style={{ fontSize: 14 }}>
                            {getOptionEmail(o)}
                        </Grid>
                        <Grid item xs={12} style={{ fontSize: 14 }}>
                            {getOptionPhoneNumber(o)}
                        </Grid>
                    </Grid>
                )}
                value={selectedStakeholder}
                clearable={clearable}
            />
        </div>
    );
};
